/**
 * CaribbeanLeaf Customer Authentication Context
 *
 * SEPARATE from staff AuthContext to maintain isolated sessions.
 * Uses CUSTOMER_TOKEN_KEY (not TOKEN_KEY) for storage.
 *
 * Features:
 * - Customer registration and login
 * - Token persistence and auto-restore
 * - Token refresh handling
 * - Verification status tracking
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  customerAuthService,
  getCustomerToken,
  clearCustomerTokens,
  CustomerAuthError,
  CUSTOMER_TOKEN_KEY,
} from '@/services/customerAuthService';
import type {
  Customer,
  CustomerLoginRequest,
  CustomerRegisterRequest,
  CustomerRegisterResponse,
} from '@/types/customer';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerAuthState {
  customer: Customer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface CustomerAuthContextValue extends CustomerAuthState {
  login: (credentials: CustomerLoginRequest) => Promise<void>;
  register: (
    data: CustomerRegisterRequest
  ) => Promise<CustomerRegisterResponse>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(
  null
);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CustomerAuthState>({
    customer: null,
    isAuthenticated: false,
    isLoading: true,
  });

  /**
   * Initialize auth state from stored CUSTOMER_TOKEN_KEY on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      const token = getCustomerToken();

      if (token) {
        try {
          // Verify token and get current customer profile
          const customer = await customerAuthService.getProfile();

          setState({
            customer,
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        } catch (error) {
          // Token invalid - clear it
          if (error instanceof CustomerAuthError && error.status === 401) {
            clearCustomerTokens();
          }
        }
      }

      setState(prev => ({ ...prev, isLoading: false }));
    };

    initAuth();
  }, []);

  /**
   * Listen for customer logout events (401 responses)
   */
  useEffect(() => {
    const handleLogout = () => {
      setState({
        customer: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener('customer:logout', handleLogout);
    return () => window.removeEventListener('customer:logout', handleLogout);
  }, []);

  /**
   * Register a new customer
   */
  const register = useCallback(
    async (
      data: CustomerRegisterRequest
    ): Promise<CustomerRegisterResponse> => {
      const response = await customerAuthService.register(data);
      // Note: Registration doesn't auto-login - customer needs to verify email first
      return response;
    },
    []
  );

  /**
   * Login with email, password, and tenant slug
   */
  const login = useCallback(async (credentials: CustomerLoginRequest) => {
    const response = await customerAuthService.login(credentials);

    // Update state with customer data
    setState({
      customer: response.customer,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  /**
   * Logout and clear all customer auth state
   */
  const logout = useCallback(async () => {
    await customerAuthService.logout();

    setState({
      customer: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  /**
   * Refresh customer profile data
   */
  const refreshProfile = useCallback(async () => {
    if (!state.isAuthenticated) return;

    try {
      const customer = await customerAuthService.getProfile();
      setState(prev => ({ ...prev, customer }));
    } catch (error) {
      // If unauthorized, logout
      if (error instanceof CustomerAuthError && error.status === 401) {
        clearCustomerTokens();
        setState({
          customer: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    }
  }, [state.isAuthenticated]);

  return (
    <CustomerAuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useCustomerAuth(): CustomerAuthContextValue {
  const context = useContext(CustomerAuthContext);

  if (!context) {
    throw new Error(
      'useCustomerAuth must be used within a CustomerAuthProvider'
    );
  }

  return context;
}

// Re-export the constant so it can be verified
export { CUSTOMER_TOKEN_KEY };
